import type {
  ComboboxData,
  ComboboxItem,
  ComboboxItemGroup,
} from "@mantine/core";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import {
  Checkbox,
  CheckboxGroup,
  type CheckboxGroupProps,
  Group,
  Stack,
} from "../../elements";

type Props<FORM_TYPE extends FieldValues> = {
  control: Control<FORM_TYPE>;
  name: FieldPath<FORM_TYPE>;
  data: ComboboxData;
  row?: boolean;
  disabled?: boolean;
  // 呼び出し元が別の場所にエラーを出す場合に、二重表示を避けるための指定
  hideError?: boolean;
} & Omit<CheckboxGroupProps, "name" | "children">;

export function CheckboxField<FORM_TYPE extends FieldValues>({
  control,
  name,
  data,
  row,
  disabled,
  color,
  hideError = false,
  ...checkboxProps
}: Props<FORM_TYPE>) {
  const CheckBoxWrapper = ({ children }: { children: React.ReactNode }) => {
    if (row) return <Group>{children}</Group>;
    return <Stack>{children}</Stack>;
  };
  const renderCheckbox = (
    item: string | ComboboxItem | ComboboxItemGroup<string | ComboboxItem>,
    i: string | number,
  ) => {
    if (typeof item === "string") {
      return (
        <Checkbox
          color={color}
          disabled={disabled}
          key={i}
          label={item}
          value={item}
        />
      );
    } else if ("label" in item && "value" in item) {
      return (
        <Checkbox
          disabled={disabled}
          key={i}
          label={item.label}
          value={item.value}
        />
      );
    } else if ("group" in item && "items" in item) {
      return renderCheckboxGroup(item, i);
    }
  };
  const renderCheckboxGroup = (
    item: { group: string; items: (string | ComboboxItem)[] },
    i: string | number,
  ) => (
    <div key={i}>
      <strong>{item.group}</strong>
      {item.items.map((groupItem, groupIndex) =>
        renderCheckbox(groupItem, `${i}-${groupIndex}`),
      )}
    </div>
  );
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <CheckboxGroup
          {...field}
          {...checkboxProps}
          error={hideError ? undefined : error?.message}
        >
          <CheckBoxWrapper>
            {data.map((item, i) => renderCheckbox(item, i))}
          </CheckBoxWrapper>
        </CheckboxGroup>
      )}
    />
  );
}
